'use client';

import { type PropsWithChildren } from 'react';
import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { theme } from '@frameio/vapor-theme';

/**
 * The AppLayoutGrid used to rely on css grid row-gap to space out the panels, but
 * this assumed that all panels would always be showing. The Header can now be
 * hidden for some users, so the gap is now being handled by adding a top border
 * to each panel to space them out. The height of this HeaderContainer is now being
 * set here to compensate for the change.
 */
const BASE_HEADER_HEIGHT = 48;
const BORDER_WIDTH = 2;

/** @todo (find-2375): clean up v4.global-navigation flag */
export const HeaderPanel = ({
  $isWebClient,
  $hidden = false,
  children,
}: PropsWithChildren<{
  $isWebClient: boolean;
  $hidden?: boolean;
}>) => {
  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const bgColor = isGlobalNavEnabled
    ? theme.colors.background.base
    : theme.colors.background.panel;

  return (
    <StyledHeaderPanel
      $backgroundColor={$isWebClient ? bgColor : undefined}
      $hidden={$hidden}
      $isWebClient={$isWebClient}
      data-testid="sections-header"
    >
      {children}
    </StyledHeaderPanel>
  );
};

export const StyledHeaderPanel = styled.div<{
  $isWebClient: boolean;
  $hidden: boolean;
  $backgroundColor: string | undefined;
}>`
  align-items: center;
  background-color: ${(p) => p.$backgroundColor};
  border: ${(p) =>
    p.$isWebClient
      ? `${theme.spaces[BORDER_WIDTH]} ${theme.spaces[BORDER_WIDTH]} 0 solid ${theme.colors.background.base}`
      : 'none'};
  border-radius: ${(p) => (p.$isWebClient ? theme.spaces[8] : 0)};
  display: flex;
  flex-flow: row nowrap;
  height: ${(p) =>
    p.$isWebClient
      ? `${BASE_HEADER_HEIGHT + BORDER_WIDTH}px`
      : `${BASE_HEADER_HEIGHT}px`};
  justify-content: flex-start;
  padding: 0 ${theme.spaces[12]};
  position: relative;

  ${theme.mediaQueries.tablet} {
    display: ${(p) => (p.$hidden ? 'none' : undefined)};
  }
`;
